import React, { useState, useMemo } from 'react';
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Check, Close, MoreVert, Search, RemoveCircleOutline } from '@mui/icons-material';
import {
  useGetUserGatesQuery,
  useAddStatsigGateOverrideMutation,
  useRemoveStatsigGateOverrideMutation,
} from '../store/api';
import type { UserGateStatusResponse } from '@store/generated/admin';

interface ProviderStatsigGatesProps {
  providerId: string;
}

export function ProviderStatsigGates({ providerId }: ProviderStatsigGatesProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all gates with override status and evaluated result for this user
  const { data: userGates, isLoading } = useGetUserGatesQuery(
    { userId: providerId },
    { skip: !providerId },
  );

  const gates = userGates?.gates ?? [];

  // Filter gates by search query
  const filteredGates = useMemo(() => {
    if (!searchQuery) return gates;
    const query = searchQuery.toLowerCase();
    return gates.filter(
      gate =>
        gate.name.toLowerCase().includes(query) ||
        gate.gate_id.toLowerCase().includes(query) ||
        gate.description?.toLowerCase().includes(query),
    );
  }, [gates, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!gates.length) {
    return <div className="p-4 text-gray-500 text-sm">No Statsig feature gates available</div>;
  }

  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <TextField
          size="small"
          placeholder="Search gates..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" className="text-gray-400" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </div>

      {/* Gates list */}
      <div className="space-y-1">
        {filteredGates.map(gate => (
          <GateRow key={gate.gate_id} gate={gate} providerId={providerId} />
        ))}
      </div>

      {filteredGates.length === 0 && (
        <div className="p-4 text-gray-500 text-sm text-center">
          No gates match &quot;{searchQuery}&quot;
        </div>
      )}
    </div>
  );
}

// Individual gate row
interface GateRowProps {
  gate: UserGateStatusResponse;
  providerId: string;
}

function GateRow({ gate, providerId }: GateRowProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const [addOverride, { isLoading: isAddingOverride }] = useAddStatsigGateOverrideMutation();
  const [removeOverride, { isLoading: isRemovingOverride }] = useRemoveStatsigGateOverrideMutation();

  const isUpdating = isAddingOverride || isRemovingOverride;

  const handleSetOverride = async (value: 'pass' | 'fail') => {
    setMenuAnchor(null);
    try {
      await addOverride({
        gateId: gate.gate_id,
        addGateOverridesRequest: {
          passing_user_ids: value === 'pass' ? [providerId] : null,
          failing_user_ids: value === 'fail' ? [providerId] : null,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to set gate override:', error);
    }
  };

  const handleRemoveOverride = async () => {
    setMenuAnchor(null);
    try {
      await removeOverride({
        gateId: gate.gate_id,
        removeGateOverridesRequest: {
          passing_user_ids: [providerId],
          failing_user_ids: [providerId],
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to remove gate override:', error);
    }
  };

  const getRowColor = () => {
    if (gate.override_status === 'pass') return 'bg-green-50 hover:bg-green-100';
    if (gate.override_status === 'fail') return 'bg-red-50 hover:bg-red-100';
    return 'bg-gray-50 hover:bg-gray-100';
  };

  const getOverrideBadge = () => {
    if (gate.override_status === 'pass') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          <Check sx={{ fontSize: 14 }} />
          FORCED PASS
        </span>
      );
    }
    if (gate.override_status === 'fail') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          <Close sx={{ fontSize: 14 }} />
          FORCED FAIL
        </span>
      );
    }
    return null;
  };

  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${getRowColor()}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-mono truncate ${
              gate.override_status ? 'font-medium' : 'text-gray-600'
            }`}
          >
            {gate.name}
          </span>
          {!gate.is_enabled && (
            <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
              disabled
            </span>
          )}
        </div>
        {gate.description && (
          <div className="text-xs text-gray-500 truncate mt-0.5">{gate.description}</div>
        )}
      </div>

      <div className="flex items-center gap-3 ml-4">
        {/* Evaluated result indicator */}
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            gate.evaluated_result
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-200 text-gray-600'
          }`}
        >
          {gate.evaluated_result ? 'PASS' : 'FAIL'}
        </span>

        {getOverrideBadge()}

        <IconButton
          size="small"
          onClick={e => setMenuAnchor(e.currentTarget)}
          disabled={isUpdating}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </div>

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => handleSetOverride('pass')} disabled={isUpdating}>
          <Check className="mr-2 text-green-600" fontSize="small" />
          Force Pass
        </MenuItem>
        <MenuItem onClick={() => handleSetOverride('fail')} disabled={isUpdating}>
          <Close className="mr-2 text-red-600" fontSize="small" />
          Force Fail
        </MenuItem>
        {gate.override_status && (
          <MenuItem onClick={handleRemoveOverride} disabled={isUpdating}>
            <RemoveCircleOutline className="mr-2 text-gray-600" fontSize="small" />
            Remove Override
          </MenuItem>
        )}
      </Menu>
    </div>
  );
}
